import React, { useEffect, useState } from 'react';
import config from '../../config';

const GoogleSignIn = ({ onSuccess, onError, text = "Sign in with Google" }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isNative, setIsNative] = useState(false);

    useEffect(() => {
        console.log('GoogleSignIn component mounted');
        console.log('Environment variables:', {
            API_URL: process.env.REACT_APP_API_URL,
            GOOGLE_CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID
        });

        // Check if we're in a native environment
        const checkNativeEnvironment = async () => {
            try {
                // Check for Capacitor
                if (window.Capacitor) {
                    console.log('Capacitor detected');
                    setIsNative(window.Capacitor.isNativePlatform());
                } else {
                    console.log('Capacitor not detected, using web mode');
                    setIsNative(false);
                }
            } catch (error) {
                console.log('Error checking platform:', error);
                setIsNative(false);
            }
        };

        checkNativeEnvironment();
    }, []);

    const handleNativeGoogleSignIn = async () => {
        try {
            console.log('=== NATIVE GOOGLE AUTH ===');

            // Import GoogleAuth dynamically
            const { GoogleAuth } = await import('@daniele-rolli/capacitor-google-auth');
            
            console.log('GoogleAuth imported successfully');

            // Initialize Google Auth
            await GoogleAuth.initialize({
                clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                scopes: ['profile', 'email'],
                grantOfflineAccess: true
            });

            console.log('GoogleAuth initialized, attempting sign in...');

            // Sign in with native Google Auth
            const result = await GoogleAuth.signIn();
            console.log('Native Google Auth result:', result);

            if (result && result.authentication && result.authentication.idToken) {
                // Use configured API URL for both native and web
                const baseApiUrl = config.api.API_URL;

                // Send the authentication token to backend
                const backendResponse = await fetch(`${baseApiUrl}/api/auth/google/verify`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ 
                        token: result.authentication.idToken,
                        accessToken: result.authentication.accessToken 
                    }),
                });

                console.log('Backend verification URL:', `${baseApiUrl}/api/auth/google/verify`);

                if (backendResponse.ok) {
                    const userData = await backendResponse.json();
                    if (onSuccess) {
                        onSuccess(userData);
                    }
                } else {
                    throw new Error('Failed to verify Google token with backend');
                }
            } else {
                throw new Error('No authentication data received from Google');
            }
        } catch (error) {
            console.error('Native Google Auth error:', error);
            if (onError) {
                onError(error);
            }
        }
    };

    const handleWebGoogleSignIn = () => {
        // For web, redirect to backend OAuth endpoint
        console.log('=== WEB GOOGLE AUTH ===');

        // Get current origin for redirect
        const currentOrigin = window.location.origin;

        // Use configured API URL for both native and web
        const baseApiUrl = config.api.API_URL;

        // Add debug logging for production troubleshooting
        console.log('OAuth Debug Info:');
        console.log('- Current Origin:', currentOrigin);
        console.log('- Base API URL:', baseApiUrl);
        console.log('- Is Production:', baseApiUrl.includes('api.aboutread.com'));

        const authUrl = `${baseApiUrl}/api/auth/google?redirect=${encodeURIComponent(currentOrigin)}`;

        console.log('Final Auth URL:', authUrl);
        console.log('URL Length:', authUrl.length);
        console.log('Encoded Redirect:', encodeURIComponent(currentOrigin));

        setTimeout(() => {
            window.location.href = authUrl;
        }, 1000);
    };

    const handleGoogleSignIn = async () => {
        console.log('=== GOOGLE SIGN IN BUTTON CLICKED ===');
        console.log('Is Native Platform:', isNative);

        setIsLoading(true);
        try {
            if (isNative) {
                await handleNativeGoogleSignIn();
            } else {
                handleWebGoogleSignIn();
            }
        } catch (error) {
            console.error('Error in handleGoogleSignIn:', error);
            if (onError) {
                onError(error);
            }
        } finally {
            // Only set loading to false for native auth, not web redirect
            if (isNative) {
                setIsLoading(false);
            }
        }
    };

    return (
        <div>
            <button
                onClick={handleGoogleSignIn}
                className="btn btn-light border w-100 d-flex align-items-center justify-content-center"
                type="button"
                disabled={isLoading}
                style={{ minHeight: '44px' }}
            >
                {isLoading ? (
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                ) : (
                    <svg
                        className="me-2"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            fill="#4285F4"
                        />
                        <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                        />
                        <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            fill="#FBBC05"
                        />
                        <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                        />
                    </svg>
                )}
                {isLoading ? 'Signing in...' : text}
            </button>
        </div>
    );
};

export default GoogleSignIn;